import { useState, useRef, useEffect } from 'react';
import { useAuth, useAuthActions } from '../state/AuthContext';
import '../styles/favorites.css';

function ArtistActionPills({ artistName, artistId, artistImage }) {
  const { user, favorites, dislikes, knownArtists, discoveredArtists } = useAuth();
  const { toggleFavorite, toggleDislike, toggleKnownArtist, toggleDiscoveredArtist, showAuthModal } = useAuthActions();
  const [favPopout, setFavPopout] = useState(false);
  const [suppressHover, setSuppressHover] = useState(false);
  const favRef = useRef(null);
  const pillsRef = useRef(null);

  const isFavorite = favorites.some((f) => f.artistName === artistName);
  const isDiscovered = discoveredArtists.some((d) => d.artistName === artistName);
  const isKnown = knownArtists.some((k) => k.artistName === artistName);
  const isDisliked = dislikes.some((d) => d.artistName === artistName);

  // Close popout on outside click
  useEffect(() => {
    function handleClick(e) {
      if (favRef.current && !favRef.current.contains(e.target)) setFavPopout(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close popout when artist changes; clear hover suppression once cursor moves
  useEffect(() => {
    setFavPopout(false);
    if (suppressHover) {
      const el = pillsRef.current;
      if (!el) return;
      const restore = () => { setSuppressHover(false); };
      el.addEventListener('pointermove', restore, { once: true });
      el.addEventListener('pointerleave', restore, { once: true });
      return () => {
        el.removeEventListener('pointermove', restore);
        el.removeEventListener('pointerleave', restore);
      };
    }
  }, [artistName, suppressHover]);

  if (!user) {
    return (
      <div className="artist-action-pills">
        <button className="action-pill action-pill-fav" onClick={() => showAuthModal('register')}>
          <StarIcon />
          <span>Favorite</span>
        </button>
        <button className="action-pill action-pill-known" onClick={() => showAuthModal('register')}>
          <NeutralFaceIcon />
          <span>Neutral</span>
        </button>
        <button className="action-pill action-pill-dislike" onClick={() => showAuthModal('register')}>
          <ThumbsDownIcon />
          <span>Dislike</span>
        </button>
      </div>
    );
  }

  // Determine favorite pill state
  const favActive = isFavorite || isDiscovered;
  const favGold = isDiscovered;

  const handleFavPillClick = (e) => {
    e.currentTarget.blur();
    if (favActive) {
      // Toggle off: remove favorite or discovered
      if (isDiscovered) {
        toggleDiscoveredArtist(artistName, artistId, artistImage);
      } else {
        toggleFavorite(artistName, artistId, artistImage);
      }
      setFavPopout(false);
    } else {
      // Activate favorite
      toggleFavorite(artistName, artistId, artistImage);
      setFavPopout(true);
    }
  };

  const handleDiscoveredClick = () => {
    // Upgrade from favorite to discovered (or set discovered directly)
    if (!isDiscovered) {
      if (isFavorite) {
        toggleFavorite(artistName, artistId, artistImage);
      }
      toggleDiscoveredArtist(artistName, artistId, artistImage);
    }
    setFavPopout(false);
  };

  const handleKnownPillClick = () => {
    if (isKnown) {
      toggleKnownArtist(artistName, artistId, artistImage);
    } else {
      // If disliked, remove dislike first
      if (isDisliked) {
        toggleDislike(artistName, artistId, artistImage);
      }
      toggleKnownArtist(artistName, artistId, artistImage);
    }
  };

  const handleDislikePillClick = (e) => {
    e.currentTarget.blur();
    if (isDisliked) {
      toggleDislike(artistName, artistId, artistImage);
    } else {
      // If known, remove known first
      if (isKnown) {
        toggleKnownArtist(artistName, artistId, artistImage);
      }
      toggleDislike(artistName, artistId, artistImage);
      // Suppress hover on the next artist so the dislike button doesn't
      // appear pre-selected just because the cursor is still in place
      setSuppressHover(true);
    }
  };

  return (
    <div className={`artist-action-pills${suppressHover ? ' suppress-hover' : ''}`} ref={pillsRef}>
      <div className="action-pill-wrapper" ref={favRef}>
        <button
          className={`action-pill action-pill-fav${favActive ? ' active' : ''}${favGold ? ' gold' : ''}`}
          onClick={handleFavPillClick}
          title={favGold ? 'Discovered on Stellar' : favActive ? 'Favorited' : 'Add to favorites'}
        >
          {favGold ? <FourPointStarIcon /> : <StarIcon />}
          <span>{favGold ? 'Discovered' : 'Favorite'}</span>
        </button>
        {favPopout && favActive && !favGold && (
          <div className="action-popout action-popout-fav">
            <button className="action-popout-btn action-popout-discovered" onClick={handleDiscoveredClick}>
              <FourPointStarIcon />
              <span>Mark as Discovered</span>
            </button>
          </div>
        )}
      </div>

      <button
        className={`action-pill action-pill-known${isKnown ? ' active' : ''}`}
        onClick={handleKnownPillClick}
        title={isKnown ? 'Neutral artist' : 'Mark as neutral'}
      >
        <NeutralFaceIcon filled={isKnown} />
        <span>Neutral</span>
      </button>

      <button
        className={`action-pill action-pill-dislike${isDisliked ? ' active' : ''}`}
        onClick={handleDislikePillClick}
        title={isDisliked ? 'Disliked' : 'Dislike this artist'}
      >
        <ThumbsDownIcon />
        <span>Dislike</span>
      </button>
    </div>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  );
}

function FourPointStarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" width="14" height="14">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
    </svg>
  );
}

function NeutralFaceIcon({ filled }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
      <circle cx="12" cy="12" r="10" fill={filled ? 'currentColor' : 'none'} />
      <circle cx="9" cy="10" r="1" fill={filled ? '#12122a' : 'currentColor'} stroke="none" />
      <circle cx="15" cy="10" r="1" fill={filled ? '#12122a' : 'currentColor'} stroke="none" />
      <line x1="8" y1="15" x2="16" y2="15" stroke={filled ? '#12122a' : 'currentColor'} />
    </svg>
  );
}

function ThumbsDownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
      <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3zm7-13h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" />
    </svg>
  );
}

export default ArtistActionPills;
